import { Box, styled, Typography } from "@material-ui/core";
import { EXAM_COLORS } from "../../_lib/epocus-exam-colors";
import { EXAM_ICONS, EXAM_LABELS } from "../../_lib/epocus-exam-constants";
import { UltrasoundScanner } from "../../_lib/epocus-icons";
import {
  EPOCUS_BLUE,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { getFirstName, PerformanceHeader } from "../../_lib/performance-header";
import {
  getScoreColor,
  SCORE_GOOD,
  SCORE_OK,
} from "../../_lib/qa-score-colors";
import {
  REVIEWER_WINDOW_OPTIONS,
  type ReviewerWindowKey,
  reviewerWindowLabel,
} from "../../_lib/reviewer-stats-window";
import { ScoreSparkline } from "../../_lib/score-sparkline";
import { SegmentedPeriodSelector } from "../../_lib/segmented-period-selector";
import {
  BigValue,
  Card,
  MetaLabel,
  SectionTitle,
  SummaryGrid,
} from "../../_lib/stat-primitives";
import { useReviewerTurnaroundStats } from "../../_lib/use-reviewer-turnaround-stats";
import { ReviewHistorySection } from "./review-history-table";
import { useReviewerReviewHistory } from "./use-reviewer-review-history";
import type { ExamTypeReviewCount } from "./use-reviewer-stats";
import { useReviewerStats } from "./use-reviewer-stats";

const EMPTY_RATE = "—";

// ─── Summary Cards ────────────────────────────────────────────────────────────

const ScansReviewedCard: FC<{ count: number; trend: number[] }> = ({
  count,
  trend,
}) => (
  <Card>
    <MetaLabel>Scans Reviewed</MetaLabel>
    <Box
      display="flex"
      alignItems="baseline"
      style={{ gap: 8, marginBottom: 8 }}
    >
      <BigValue>{count}</BigValue>
      <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
        scans
      </Typography>
    </Box>
    <ScoreSparkline data={trend} color={EPOCUS_BLUE} />
  </Card>
);

const AverageScoreCard: FC<{ score: string; trend: number[] }> = ({
  score,
  trend,
}) => (
  <Card>
    <MetaLabel>Average Score Given</MetaLabel>
    <Box
      display="flex"
      alignItems="baseline"
      style={{ gap: 8, marginBottom: 8 }}
    >
      <BigValue style={{ color: getScoreColor(score) }}>{score}</BigValue>
      {score !== "—" && (
        <Typography style={{ fontSize: rem(14), color: EPOCUS_TEXT_SECONDARY }}>
          / 5
        </Typography>
      )}
    </Box>
    <ScoreSparkline data={trend} color={SCORE_GOOD} />
  </Card>
);

const WithinTargetCard: FC<{
  withinTargetPct: number | null;
  targetMinutes: number;
  windowLabel: string;
}> = ({ withinTargetPct, targetMinutes, windowLabel }) => (
  <Card>
    <MetaLabel>Within Target</MetaLabel>
    <Box
      display="flex"
      alignItems="baseline"
      style={{ gap: 8, marginBottom: 8 }}
    >
      <BigValue>
        {withinTargetPct === null ? EMPTY_RATE : `${withinTargetPct}%`}
      </BigValue>
    </Box>
    <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
      {withinTargetPct === null
        ? "No signed reviews yet"
        : `Signed within ${targetMinutes} min · ${windowLabel}`}
    </Typography>
  </Card>
);

// ─── Reviews by Exam Type ─────────────────────────────────────────────────────

const ExamTypeGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
});

const ExamTypeCountCard: FC<{ stats: ExamTypeReviewCount }> = ({ stats }) => {
  const { examType, count } = stats;
  const ExamIcon = EXAM_ICONS[examType] ?? UltrasoundScanner;

  return (
    <Card style={{ padding: "14px 14px 12px" }}>
      <Box display="flex" alignItems="center" style={{ gap: 6 }}>
        <ExamIcon
          width={18}
          height={18}
          fill={EPOCUS_ICON_ACTIVE}
          aria-hidden
        />
        <Typography
          style={{
            fontSize: rem(12),
            fontWeight: 600,
            color: EPOCUS_TEXT_PRIMARY,
          }}
        >
          {EXAM_LABELS[examType]}
        </Typography>
      </Box>
      <Typography
        style={{
          fontSize: rem(22),
          fontWeight: 700,
          color: EXAM_COLORS[examType],
          marginTop: 6,
        }}
      >
        {count}
      </Typography>
    </Card>
  );
};

const ByExamTypeSection: FC<{ byExamType: ExamTypeReviewCount[] }> = ({
  byExamType,
}) => (
  <Box>
    <SectionTitle>Reviews by Exam Type</SectionTitle>
    {byExamType.length === 0 ? (
      <Card>
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
          No reviews yet
        </Typography>
      </Card>
    ) : (
      <ExamTypeGrid>
        {byExamType.map((stats) => (
          <ExamTypeCountCard key={stats.examType} stats={stats} />
        ))}
      </ExamTypeGrid>
    )}
  </Box>
);

// ─── Feedback Tags ────────────────────────────────────────────────────────────

const FeedbackTagsSection: FC<{
  tags: Array<{ tag: string; pct: number }>;
}> = ({ tags }) => (
  <Box>
    <SectionTitle>Feedback I Give Most</SectionTitle>
    <Card>
      {tags.length === 0 ? (
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
          No reviews yet
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
          {tags.map(({ tag, pct }) => (
            <Box
              key={tag}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                style={{ fontSize: rem(13), color: EPOCUS_TEXT_PRIMARY }}
              >
                {tag}
              </Typography>
              <Typography
                style={{
                  fontSize: rem(13),
                  fontWeight: 700,
                  color: pct >= 50 ? SCORE_GOOD : SCORE_OK,
                }}
              >
                {pct}%
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Card>
  </Box>
);

// ─── Variant ──────────────────────────────────────────────────────────────────

export const ReviewerPerformance: FC<{
  reviewerId: string;
  name: string | undefined;
  windowKey: ReviewerWindowKey;
  onWindowChange: (key: ReviewerWindowKey) => void;
}> = ({ reviewerId, name, windowKey, onWindowChange }) => {
  const windowLabel = reviewerWindowLabel(windowKey);

  const {
    scansReviewed,
    averageScoreGiven,
    scoreTrend,
    volumeTrend,
    topFeedbackTags,
    byExamType,
  } = useReviewerStats(reviewerId, windowKey);
  const { withinTargetPct, targetMinutes } =
    useReviewerTurnaroundStats(windowKey);
  const history = useReviewerReviewHistory(reviewerId, windowKey);

  return (
    <>
      <PerformanceHeader
        title="My Performance"
        subtitle={`${getFirstName(name)} · reviewing activity · ${windowLabel}`}
      />

      <SegmentedPeriodSelector<ReviewerWindowKey>
        options={REVIEWER_WINDOW_OPTIONS}
        value={windowKey}
        onChange={onWindowChange}
        label="Date range"
      />

      <SummaryGrid>
        <ScansReviewedCard count={scansReviewed} trend={volumeTrend} />
        <AverageScoreCard score={averageScoreGiven} trend={scoreTrend} />
        <WithinTargetCard
          withinTargetPct={withinTargetPct}
          targetMinutes={targetMinutes}
          windowLabel={windowLabel}
        />
      </SummaryGrid>

      <ByExamTypeSection byExamType={byExamType} />

      <ReviewHistorySection rows={history} windowLabel={windowLabel} />

      <FeedbackTagsSection tags={topFeedbackTags} />
    </>
  );
};
